'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import StatusNotificationBadge from './StatusNotificationBadge';
import UserContextIndicator from './UserContextIndicator';

type UserRole = 'broker' | 'admin';

interface HeaderProps {
  userRole?: UserRole;
  userName?: string;
  brokerageName?: string;
  notificationCount?: number;
  isMenuOpen?: boolean;
  onMenuToggle?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  roles: UserRole[];
  notificationKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/broker-dashboard',
    icon: 'HomeIcon',
    roles: ['broker'],
  },
  {
    label: 'Cases',
    path: '/case-detail-management',
    icon: 'DocumentTextIcon',
    roles: ['broker'],
  },
  {
    label: 'New Case',
    path: '/case-creation-wizard',
    icon: 'PlusCircleIcon',
    roles: ['broker'],
  },
  {
    label: 'Control Center',
    path: '/admin-control-center',
    icon: 'AdjustmentsHorizontalIcon',
    roles: ['admin'],
  },
  {
    label: 'Brokerages',
    path: '/brokerage-management',
    icon: 'BuildingOfficeIcon',
    roles: ['admin'],
  },
  {
    label: 'Reports',
    path: '/admin-reports-analytics',
    icon: 'ChartBarIcon',
    roles: ['admin'],
  },
];

const Header = ({
  userRole = 'broker',
  userName = 'James Mitchell',
  brokerageName = 'Pinnacle Pension Advisors',
  notificationCount = 3,
  isMenuOpen = false,
  onMenuToggle,
}: HeaderProps) => {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(isMenuOpen);

  const filteredNavItems = NAV_ITEMS.filter((item) =>
    item.roles.includes(userRole)
  );

  const handleMenuToggle = () => {
    setMobileMenuOpen((prev) => !prev);
    onMenuToggle?.();
  };

  const isActive = (path: string) => pathname === path;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-primary shadow-elevation-3 h-16">
        <div className="flex items-center justify-between h-full px-6">
          {/* Logo */}
          <Link href={userRole === 'admin' ? '/admin-control-center' : '/broker-dashboard'} className="flex items-center gap-3 flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm">
            <div className="flex items-center justify-center w-9 h-9 rounded-md bg-accent">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11 2L3 7V15L11 20L19 15V7L11 2Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M11 7V15M7.5 9L11 7L14.5 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-700 text-white text-lg tracking-tight leading-none">QRSL</span>
              <span className="block font-caption text-white/70 text-xs tracking-wide">Pension Portal</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 flex-1 ml-10" aria-label="Primary navigation">
            {filteredNavItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all duration-[250ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-primary
                  ${isActive(item.path)
                    ? 'bg-white/15 text-white' :'text-white/75 hover:text-white hover:bg-white/10'
                  }`}
              >
                <Icon name={item.icon as any} size={18} variant="outline" />
                <span>{item.label}</span>
                {item.notificationKey && notificationCount > 0 && (
                  <StatusNotificationBadge count={notificationCount} size="sm" />
                )}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-accent rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Compliance badge - desktop */}
            <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/10 border border-white/15">
              <Icon name="ShieldCheckIcon" size={14} variant="solid" className="text-success" />
              <span className="font-caption text-white/80 text-xs">CBI Registered</span>
            </div>

            {/* Notifications */}
            <button
              className="relative flex items-center justify-center w-10 h-10 rounded-sm text-white/75 hover:text-white hover:bg-white/10 transition-all duration-[250ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-primary"
              aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
            >
              <Icon name="BellIcon" size={20} variant="outline" />
              {notificationCount > 0 && (
                <StatusNotificationBadge count={notificationCount} size="sm" className="absolute -top-0.5 -right-0.5" />
              )}
            </button>

            {/* User context */}
            <UserContextIndicator
              userName={userName}
              brokerageName={brokerageName}
              userRole={userRole}
            />

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-sm text-white/75 hover:text-white hover:bg-white/10 transition-all duration-[250ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-primary"
              onClick={handleMenuToggle}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} variant="outline" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[99] md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <nav
        id="mobile-nav"
        className={`fixed top-16 left-0 right-0 z-[99] md:hidden bg-primary shadow-elevation-4 transition-all duration-[250ms] ease-out overflow-hidden ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
        aria-label="Mobile navigation"
      >
        <div className="px-4 py-3 space-y-1 border-t border-white/10">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-[250ms] ease-out min-h-[44px]
                ${isActive(item.path)
                  ? 'bg-white/15 text-white' :'text-white/75 hover:text-white hover:bg-white/10'
                }`}
            >
              <Icon name={item.icon as any} size={20} variant="outline" />
              <span>{item.label}</span>
              {item.notificationKey && notificationCount > 0 && (
                <StatusNotificationBadge count={notificationCount} size="sm" className="ml-auto" />
              )}
            </Link>
          ))}
          <div className="pt-2 pb-1 border-t border-white/10 mt-2">
            <div className="flex items-center gap-2 px-4 py-2">
              <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-success" />
              <span className="font-caption text-white/70 text-xs">CBI Registered &amp; Compliant</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to offset fixed header */}
      <div className="h-16" aria-hidden="true" />
    </>
  );
};

export default Header;